import apiClient from './apiClient';

const API_URL = '/clientes';

const normalizarCuil = (valor = '') => String(valor).replace(/\D/g, '');
const normalizeItems = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.content)) return payload.content;
    if (payload && Array.isArray(payload.data)) return payload.data;
    return [];
};

// =====================
// 🔹 OBTENER CLIENTES
// =====================

// Todos los clientes (ordenados por fecha ingreso DESC)
export const getClientes = async () => {
    const response = await apiClient.get(API_URL, {
        params: {
            page: 0,
            size: 2000,
            sort: 'fechaIngreso,desc',
        },
    });
    return normalizeItems(response.data);
};

// Solo activos (paginado + filtros servidor)
export const getClientesActivos = async (page = 0, size = 15, cuil = '', nombreApellido = '', idTipoTramite = null, referencia = '') => {
    const cuilNormalizado = normalizarCuil(cuil);
    const params = {
        page,
        size,
        cuil: cuilNormalizado,
        nombreApellido,
        inactivos: false,
        sort: 'fechaIngreso,desc',
    };
    if (idTipoTramite !== null && idTipoTramite !== '') {
        params.idTipoTramite = idTipoTramite;
    }
    if (referencia) {
        params.referencia = referencia;
    }
    const response = await apiClient.get(API_URL, { params });
    return response.data;
};

// Solo inactivos (paginado + filtros servidor)
export const getClientesInactivos = async (page = 0, size = 15, cuil = '', nombreApellido = '') => {
    const cuilNormalizado = normalizarCuil(cuil);
    const response = await apiClient.get(API_URL, {
        params: {
            page,
            size,
            cuil: cuilNormalizado,
            nombreApellido,
            inactivos: true,
            sort: 'fechaIngreso,desc',
        },
    });
    return response.data;
};

// =====================
// 🔹 CRUD
// =====================

export const crearCliente = async (clienteDto) => {
    const response = await apiClient.post(API_URL, clienteDto);
    return response.data;
};

// Buscar cliente por CUIL
export const getClientePorCuil = async (cuil) => {
    const response = await apiClient.get(`${API_URL}/${cuil}`);
    return response.data;
};

// ¿Ya hay un cliente con este CUIL? Devuelve { existe, activo, apellido, nombre }. A diferencia de
// getClientePorCuil no trae el cliente completo ni falla cuando no existe (que es el caso normal al
// registrar), así que sirve para avisar del duplicado mientras se escribe el CUIL.
export const consultarExistenciaCuil = async (cuil) => {
    const response = await apiClient.get(`${API_URL}/${normalizarCuil(cuil)}/existe`);
    return response.data;
};

// Importación de clientes desde el CSV "TURNOS ANSES". simulacion=true no persiste nada:
// devuelve la previsualización (nuevos / ya existen / sin CUIL) para revisar antes de confirmar.
export const importarClientesAnsesCsv = async (archivo, simulacion = true) => {
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('simulacion', String(simulacion));
    const response = await apiClient.post(`${API_URL}/importar-anses-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

// Domicilio DNI del cliente, liviano (no trae el resto del cliente) — usado como referencia en
// comparaciones puntuales, ej. domicilio del causante en Pensión.
export const getDomicilioDniCliente = async (cuil) => {
    const response = await apiClient.get(`${API_URL}/${cuil}/domicilio-dni`);
    return response.data;
};

// Buscar cliente por nombre o apellido
export const buscarClientes = async (criterio) => {
    if (!criterio || criterio.trim() === '') return [];
    const response = await apiClient.get(`${API_URL}/buscar`, { params: { criterio } });
    return response.data;
};

// Asigna una referencia del catálogo a un cliente (y la heredan los clientes que trajo). Lo usa
// el alta cuando el referente elegido tiene la referencia en texto libre: sin esto el nuevo
// cliente heredaría "OTRO CLIENTE" en vez del canal real.
export const asignarReferenciaCliente = async (cuil, idReferencia) => {
    const response = await apiClient.put(`${API_URL}/${cuil}/referencia`, { idReferencia });
    return response.data;
};

// Modificar un cliente existente
export const modificarCliente = async (cuil, cliente) => {
    const response = await apiClient.put(`${API_URL}/${cuil}`, cliente);
    return response.data;
};

// Eliminar (baja lógica) un cliente por CUIL
// Qué se cancelaría (trámites y turnos vivos) si se confirma la baja: para el modal previo.
export const previsualizarBajaCliente = async (cuil) => {
    const response = await apiClient.get(`${API_URL}/${cuil}/baja-preview`);
    return response.data;
};

export const eliminarCliente = async (cuil, observacion = '') => {
    const response = await apiClient.delete(`${API_URL}/${cuil}`, {
        // Enviamos un objeto, igual que en reactivarCliente
        data: { observacion }  // 👈 Envuelve la observación en un objeto
    });
    return response.data;
};

export const reactivarCliente = async (cuil, observacion = '') => {
    const response = await apiClient.put(`${API_URL}/${cuil}/reactivar`, { observacion });
    return response.data;
};

// =====================
// 🔒 SEGURIDAD
// =====================
export const revelarClaves = async (cuil, password) => {
    // Llama al endpoint seguro enviando la contraseña en el cuerpo
    const response = await apiClient.post(`${API_URL}/${cuil}/revelar-claves`, { password });
    return response.data; // Retorna el objeto con las claves desencriptadas
};


// =====================
// 🔹 UTILIDAD DTO
// =====================
export function construirClienteDto(form) {
    const dto = {
        cuil: form.cuil,
        apellido: form.apellido,
        nombre: form.nombre,
        fechaIngreso: form.fechaIngreso,
        mail: form.mail || null,
        art: form.art,
        dosDoce: form.dosDoce,
        telefonoPropio: form.nroTelefono || null,
        referencia: form.referencia || form.referenciaNombre || null,
        observacion: form.observacion || null,
        oficio: form.oficio || null,
        activo: true, // Los nuevos clientes siempre son activos
    };

    if (form.nivelEducativoId) {
        dto.nivelEducativo = { id: Number(form.nivelEducativoId) };
    }

    if (form.calle && form.localidadId) {
        dto.domicilio = {
            calle: form.calle,
            altura: form.altura ? Number(form.altura) : null,
            piso: form.piso ? Number(form.piso) : null,
            departamento: form.departamento || null,
            localidad: { id: Number(form.localidadId) },
        };
    }

    if (form.clavesAfip || form.clavesSS || form.clavesCidi) {
        dto.clave = {
            claveAfip: form.clavesAfip || null,
            claveSs: form.clavesSS || null,
            claveCidi: form.clavesCidi || null,
        };
    }

    return dto;
}
/** Las filas de planilla de los trámites del cliente que vinieron de una importación. */
export const getFilasPlanillaCliente = async (cuil) => {
    const response = await apiClient.get(`${API_URL}/${cuil}/filas-planilla`);
    return Array.isArray(response.data) ? response.data : [];
};
